/**
 * Every keyboard shortcut Paige has, in plain words: the one list the F1 sheet shows
 * (ShortcutSheet.tsx), in the main window and in each window a panel was moved to. A row names
 * the windows where its keys really work, so a moved panel's sheet lists only its own keys.
 *
 * Shortcuts audit (October 6, 2026; owner: "fix the shortcuts that don't work and add the ones
 * that should"): the sheet left out the keys people press every minute (Enter and Esc in a
 * field, the table keys, Ctrl+B joining on page 1, Ctrl+C), so they are all here now.
 * Plain Windows keys every app has (Tab, Space, the arrows in a list or menu) are not listed.
 */

/** The window a sheet is opened in: the main window, or a review panel moved to its own window. */
export type ShortcutWindow = 'main' | 'viewer' | 'fields' | 'line-items';

export type ShortcutRow = {
  /** Keys pressed together are joined by "+"; alternatives are separated by " / ". */
  keys: string;
  action: string;
  note?: string;
  where: readonly ShortcutWindow[];
};
export type ShortcutGroup = { title: string; rows: readonly ShortcutRow[] };

const ALL: readonly ShortcutWindow[] = ['main', 'viewer', 'fields', 'line-items'];
const MAIN: readonly ShortcutWindow[] = ['main'];

export const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    title: 'Checking documents',
    rows: [
      {
        keys: 'Ctrl+Enter',
        action: 'Confirm & next',
        note: "Next when it's already confirmed",
        where: ALL,
      },
      { keys: 'F6 / Shift+F6', action: 'Next/previous thing to check', where: ['main', 'fields'] },
      {
        keys: 'Ctrl+B',
        action: 'Split or join',
        note: 'On page 1, it joins the one before',
        where: ['main', 'viewer'],
      },
      { keys: 'R', action: 'Turn page', note: 'With the page selected', where: MAIN },
      {
        keys: 'Ctrl+Z',
        action: 'Undo',
        note: 'Only your last value, split, join, page, or Back',
        where: ALL,
      },
    ],
  },
  {
    title: 'Typing values',
    rows: [
      { keys: 'Enter', action: 'Save and go to the next field', where: ['main', 'fields'] },
      {
        keys: 'Esc',
        action: 'Put the field back',
        note: 'As it was before you typed',
        where: ['main', 'fields', 'line-items'],
      },
      {
        keys: 'Enter / ↓',
        action: 'Save the cell and go down',
        note: 'In line items',
        where: ['main', 'line-items'],
      },
      {
        keys: 'Shift+Enter / ↑',
        action: 'Save the cell and go up',
        note: 'In line items',
        where: ['main', 'line-items'],
      },
      {
        keys: 'Alt+↑ / Alt+↓',
        action: 'Move a field up or down',
        note: 'With its handle selected',
        where: ['main', 'fields'],
      },
    ],
  },
  {
    title: 'Moving around',
    rows: [
      {
        keys: 'Ctrl+PgDn / Ctrl+PgUp',
        action: 'Next/previous document',
        note: 'Skip goes to the next one asked about',
        where: ALL,
      },
      { keys: 'PgDn / PgUp', action: 'Next/previous page', where: ALL },
      {
        keys: 'Ctrl+F',
        action: 'Find on this page',
        note: 'Enter or Shift+Enter: next or previous match',
        where: ['main', 'viewer'],
      },
      { keys: 'Ctrl+Shift+F', action: 'Search documents', where: MAIN },
      {
        keys: 'Ctrl+= / Ctrl+-',
        action: 'Zoom in or out',
        note: 'Or Ctrl + mouse wheel on the page',
        where: MAIN,
      },
      {
        keys: 'Ctrl+= / Ctrl+-',
        action: 'Zoom in or out',
        note: 'Or the mouse wheel on the page',
        where: ['viewer'],
      },
      { keys: 'Ctrl+0', action: 'Fit the page', where: ['main', 'viewer'] },
      { keys: 'Ctrl+C', action: 'Copy the selected page text', where: ['main', 'viewer'] },
    ],
  },
  {
    title: 'Everywhere',
    rows: [
      { keys: 'F1 / ?', action: 'Show this sheet', where: ALL },
      { keys: 'Ctrl+,', action: 'Settings', where: MAIN },
      { keys: 'Esc', action: 'Close a window', where: ALL },
      {
        keys: 'F2',
        action: 'Rename the job',
        note: 'On its card, or in the job switcher',
        where: MAIN,
      },
      { keys: 'Delete', action: 'Delete a job', note: 'On its card in Scan jobs', where: MAIN },
      { keys: 'F11', action: 'Full screen', where: ALL },
      { keys: 'Ctrl+M', action: 'Minimize', where: ALL },
      { keys: 'Ctrl+W', action: 'Close this window', where: ALL },
    ],
  },
];

/** The groups whose keys work in this window, each with only those rows. */
export function shortcutGroups(where: ShortcutWindow = 'main'): ShortcutGroup[] {
  return SHORTCUT_GROUPS.map((group) => ({
    title: group.title,
    rows: group.rows.filter((row) => row.where.includes(where)),
  })).filter((group) => group.rows.length > 0);
}

/**
 * Whether this key press opens (or closes) the sheet: F1 anywhere, and ? (the Gmail and GitHub
 * habit) only when it is not a letter being typed into a text box. The caller also leaves ? alone
 * while another window is open over the screen.
 */
export function shortcutSheetKey(
  event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'repeat' | 'target'>,
): 'F1' | '?' | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.key === 'F1') return 'F1';
  if (event.key !== '?' || event.repeat) return null;
  const target = event.target as { closest?: (selector: string) => unknown } | null;
  return target?.closest?.("input, textarea, select, [contenteditable='true']") ? null : '?';
}
